import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import debounce from 'lodash/debounce';
import { Card, Grid } from '@mui/material';
import ActionsPopover from 'components/ActionsPopover/ActionsPopover';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import AddFAQ from './components/AddFAQ';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Reorder from '@mui/icons-material/Reorder';

import {
  getFAQsList,
  addFAQ,
  updateFAQ,
  deleteFAQ,
  resetFAQsList,
} from '../../store/reducers/faqsSlice';
import PreviewFAQ from './components/PreviewFAQ';
import { faqsColumns } from 'constants/columns.constants';

const FAQs = () => {
  const [isLoading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const { faqsList } = useSelector((state) => state.faqs);

  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [isEditFAQ, setEditFAQ] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [isPreviewModal, setPreviewModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const columns = useMemo(() => {
    return faqsColumns;
  }, []);

  const rows =
    faqsList?.faqData?.length > 0
      ? faqsList?.faqData?.map((faq) => {
          return {
            id: faq?.id,
            question: (
              <div style={{ maxWidth: 'max(calc(100vw - 650px), 20ch)' }}>
                {faq?.question ?? 'NA'}
              </div>
            ),
            priorityIndex: faq?.priorityIndex ?? 'NA',
            actions: (
              <ActionsPopover
                viewTitle={'View FAQ'}
                editTitle={'Edit FAQ'}
                deleteTitle={'Delete FAQ'}
                onEditClick={() => {
                  setSelectedFAQ(faq);
                  setEditFAQ(true);
                  setOpenModal(true);
                }}
                onViewClick={() => {
                  setSelectedFAQ(faq);
                  setPreviewModal(true);
                }}
                onDeleteClick={() => {
                  setSelectedFAQ(faq);
                  setDeleteModal(true);
                }}
              />
            ),
          };
        })
      : [];

  async function fetchFAQsList() {
    try {
      setLoading(true);
      await dispatch(
        getFAQsList({
          page: currentPage + 1,
          limit: perPage,
          sortField: sortBy?.id ?? 'id',
          sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
          search: searchTerm,
        })
      );
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onCloseModal = () => {
    setSelectedFAQ(null);
    setEditFAQ(false);
    setOpenModal(false);
    setDeleteModal(false);
    setPreviewModal(false);
  };

  const onCreateFAQ = async (data) => {
    try {
      await dispatch(
        addFAQ({
          ...data,
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchFAQsList();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const onUpdateFAQ = async (data) => {
    try {
      await dispatch(
        updateFAQ({
          ...data,
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchFAQsList();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const onDeleteFAQ = async () => {
    try {
      await dispatch(deleteFAQ(selectedFAQ?.id));
      if (currentPage === 0) {
        fetchFAQsList();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      onCloseModal();
    }
  };

  const onFetchDataDebounced = debounce(fetchFAQsList, 0);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, searchTerm, perPage]);

  useEffect(() => {
    return () => {
      dispatch(resetFAQsList());
    };
  }, []);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Frequently Asked Questions
              </MDTypography>
              <MDButton
                sx={{ marginLeft: 'auto', marginRight: '16px' }}
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenModal(true)}
              >
                Add FAQ
              </MDButton>
              <Link to={'/faq/reorder'}>
                <MDButton variant="outlined" startIcon={<Reorder />}>
                  Reorder FAQs
                </MDButton>
              </Link>
            </MDBox>
            <MDBox
              sx={{
                position: 'relative',
                paddingTop: '85px',
                paddingBottom: {
                  xs: '150px',
                  sm: '95px',
                },
              }}
            >
              <DataTable
                table={{ columns, rows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: perPage }}
                showTotalEntries
                canSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                perPage={perPage}
                setPerPage={setPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={faqsList?.totalPage}
                totalRecords={faqsList?.totalCount}
                hiddenColumns={['id']}
                queryPageSortBy={[sortBy]}
                setSortBy={setSortBy}
                isLoading={isLoading}
                // isDraggable
                // onDragRow={onDragEnd}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
      {openModal && (
        <AddFAQ
          openModal={openModal}
          onCloseModal={onCloseModal}
          onCreateFAQ={onCreateFAQ}
          onUpdateFAQ={onUpdateFAQ}
          isEdit={isEditFAQ}
          selectedFAQ={selectedFAQ}
        />
      )}

      {isDeleteModal && (
        <ConfirmModal
          open={isDeleteModal}
          onCloseModal={onCloseModal}
          modalText="Are you sure you want to delete this FAQ?"
          okayText="Yes, delete"
          cancelText="No, cancel"
          onOkayClick={onDeleteFAQ}
        />
      )}
      {isPreviewModal && (
        <PreviewFAQ
          openModal={isPreviewModal}
          onCloseModal={onCloseModal}
          selectedFAQ={selectedFAQ}
        />
      )}
    </MDBox>
  );
};

export default FAQs;
